import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { SalesService } from '../../../_services/sales/sales.service';
import { SalesFilterService, SalesFilters } from '../sales-filter.service';
import ISale from '../../../_interfaces/ISale';
import IPatient from '../../../_interfaces/IPatients';

// PrimeNG
import { TableModule, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DialogService } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { DrawerModule } from 'primeng/drawer';
import { DividerModule } from 'primeng/divider';

// Components
import { FormSaleComponent } from '../../../components/forms/form-sale/form-sale.component';
import { DeleteButtonSaleComponent } from '../../../components/delete-buttons/delete-button-sale/delete-button-sale.component';
import { PatientLinkComponent } from '../../../components/misc/patient-link/patient-link.component';
import { ProductImageComponent } from '../../../components/product-image/product-image.component';

@Component({
  standalone: true,
  selector: 'app-sales-list',
  templateUrl: './sales-list.component.html',
  styleUrls: ['./sales-list.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    TooltipModule,
    TagModule,
    SkeletonModule,
    DrawerModule,
    DividerModule,
    DeleteButtonSaleComponent,
    PatientLinkComponent,
    ProductImageComponent,
  ],
  providers: [DialogService],
})
export class SalesListComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('salesTable') salesTable: Table;

  // Data
  public sales: ISale[] = [];
  public filteredSales: ISale[] = [];
  public loading: boolean = true;

  // Mobile
  public isMobile: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(
    public session: SessionService,
    private salesService: SalesService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private filterService: SalesFilterService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.checkMobile();
    this.loadSales();
    this.setupRefreshListener();

    // Subscribe to filter changes from parent
    this.subscriptions.push(
      this.filterService.filters$.subscribe((filters) => {
        this.applyFilters(filters);
      }),
    );

    // Handle resize
    window.addEventListener('resize', () => this.checkMobile());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    window.removeEventListener('resize', () => this.checkMobile());
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private setupRefreshListener(): void {
    const handler = () => this.loadSales();
    window.addEventListener('sales:refresh', handler);
    this.subscriptions.push({
      unsubscribe: () => window.removeEventListener('sales:refresh', handler),
    } as Subscription);
  }

  loadSales(): void {
    this.loading = true;

    // Fetch all sales and filter client-side for accurate date filtering
    this.salesService.findBySite(this.customer.id, this.site.id, {}).subscribe({
      next: (sales) => {
        this.sales = sales;
        this.applyFilters(this.filterService.currentFilters);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  applyFilters(filters: SalesFilters): void {
    let filtered = [...this.sales];

    // Client-side date filtering
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = moment(filters.dateRange[0]).startOf('day');
      const endDate = moment(filters.dateRange[1]).endOf('day');

      filtered = filtered.filter((sale) => {
        const saleDate = moment(sale.sales_date);
        return (
          saleDate.isSameOrAfter(startDate) && saleDate.isSameOrBefore(endDate)
        );
      });
    }

    // Text search
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      filtered = filtered.filter((sale) => {
        const patientName = sale.patient
          ? `${sale.patient.name} ${sale.patient.surname}`.toLowerCase()
          : '';
        const productName = sale.product?.name?.toLowerCase() || '';
        // Handle nested treatment structure
        let treatmentName = '';
        if (sale.treatment) {
          if ((sale.treatment as any).treatment?.name) {
            treatmentName = (
              sale.treatment as any
            ).treatment.name.toLowerCase();
          } else if (sale.treatment.name) {
            treatmentName = sale.treatment.name.toLowerCase();
          }
        }
        return (
          patientName.includes(search) ||
          productName.includes(search) ||
          treatmentName.includes(search)
        );
      });
    }

    this.filteredSales = filtered;
  }

  editSale(sale: ISale): void {
    const ref = this.dialogService.open(FormSaleComponent, {
      closable: true,
      modal: true,
      header: this.translate.instant('SALES.TITLE.EDIT'),
      width: this.isMobile ? '95%' : '52rem',
      styleClass: 'vc-dialog-lg vc-dialog--sale',
      inputValues: {
        payload: sale,
        onUpdate: () => {
          ref.close();
          this.loadSales();
        },
      },
    });
  }

  onDelete = (): void => {
    this.loadSales();
  };

  getPatientFullName(patient: IPatient): string {
    if (!patient) return '-';
    return `${patient.name} ${patient.surname || ''}`.trim();
  }

  getProductOrTreatment(sale: ISale): string {
    // Handle product
    if (sale.product) {
      if (typeof sale.product === 'object' && sale.product.name) {
        return sale.product.name;
      }
      if (typeof sale.product === 'string') {
        return sale.product;
      }
    }
    // Handle nested treatment structure (patient-has-treatment with treatment inside)
    if (sale.treatment) {
      if (typeof sale.treatment === 'object') {
        // Check if it's a nested structure (patient-has-treatment)
        const treatmentAny = sale.treatment as any;
        if (
          treatmentAny.treatment &&
          typeof treatmentAny.treatment === 'object' &&
          treatmentAny.treatment.name
        ) {
          return treatmentAny.treatment.name;
        }
        // Direct treatment name
        if (sale.treatment.name && typeof sale.treatment.name === 'string') {
          return sale.treatment.name;
        }
      }
      if (typeof sale.treatment === 'string') {
        return sale.treatment;
      }
    }
    return '-';
  }

  formatDate(date: Date): string {
    return moment(date).format('DD/MM/YYYY');
  }

  formatPrice(sale: ISale): string {
    const price = typeof sale.price === 'number' ? sale.price : 0;
    const symbol = sale.currency?.symbol || '€';
    return `${price.toFixed(2)} ${symbol}`;
  }

  getProductImage(sale: ISale): string {
    return sale.product?.image_url || 'assets/images/default/product.png';
  }

  hasPatient(sale: ISale): boolean {
    return !!sale.patient;
  }

  getPaymentMethodName(sale: ISale): string {
    if (!sale.paymentType) {
      return '-';
    }

    let paymentKey: string = '';

    if (typeof sale.paymentType === 'object' && sale.paymentType.name) {
      paymentKey =
        typeof sale.paymentType.name === 'string' ? sale.paymentType.name : '';
    } else if (typeof sale.paymentType === 'string') {
      paymentKey = sale.paymentType;
    }

    if (!paymentKey) {
      return '-';
    }

    // Try to get translation from PAYMENT_TYPES
    const translationKey = `PAYMENT_TYPES.${paymentKey.toUpperCase()}`;
    const translated = this.translate.instant(translationKey);

    // If translation exists and is different from key, use it
    return translated !== translationKey ? translated : paymentKey;
  }

  get totalAmount(): number {
    return this.filteredSales.reduce((sum, sale) => sum + sale.price, 0);
  }

  get salesCount(): number {
    return this.filteredSales.length;
  }
}
